import toastMessage from '@/components/Toast';
import { getThunkError } from '@/store/utility/thunkErrorUtils';
import { extractErrorMessage, thunkError } from '@/utils/thunkError';
import apiInstance from '@api/index';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppDispatch } from '@store/store';

import {
  removeLocalAmortRow,
  setTransactionAmortSchedules,
  upsertLocalAmortRow,
} from './hedgeAccountingSlice';
import {
  BulkTransactionTitle,
  BulkTransactionTitlesRequest,
  BulkTransactionTitlesResponse,
  CalculateAmortEffectiveRatePayload,
  CompanyEntityHedgingStandard,
  CreateJournalEntryMovementsPayload,
  CreateTransactionAmortPayload,
  CreateTransactionAmortSetSchedulePayload,
  CreateTransactionNoHedgeAccPayload,
  EditJournalEntryMovementCommentPayload,
  HedgeAccountingState,
  HedgeAmortData,
  InitAmortEffectiveRatePayload,
  InitAmortEffectiveRateResponse,
  JournalEntry,
  JournalEntryCommand,
  JournalEntryMovement,
  JournalEntryMovementStandard,
  PatchTransactionAmortPayload,
  PatchTransactionEffectivenessPayload,
  SearchTransactionAmortSetSchedulePayload,
  TradeDesignationDate,
  TradeIdsFetchPayload,
  TransactionAmortSetScheduleData,
  TransactionAmortType,
  TransactionEffectivenessData,
  TransactionEffectivenessPayload,
  TransactionLeg,
  TransactionRelation,
  TransactionTitle,
  TransactionTitlePayload,
  TransactionWithoutHedgeAcc,
  UpdateTransactionAmortPayload,
  UpdateTransactionEffectivenessPayload,
  UpdateTransactionFieldsPayload,
  UpdateTransactionNoHedgeAccPayload,
} from './types';
import {
  buildCreatePayloadsFromReferenceEffectiveness,
  getHypotheticalRowsFromRelation,
  getSelectedTypesFromReferenceJeData,
  groupEffectivenessByTradeId,
  groupRelationsByTradeId,
  hasMeaningfulEffectivenessData,
  hasMeaningfulHypotheticalData,
} from './utils';

export const fetchJournalEntries = createAsyncThunk<
  JournalEntry[],
  TradeIdsFetchPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/fetchJournalEntries',
  async ({ tradeIds }, { rejectWithValue }) => {
    try {
      if (!Array.isArray(tradeIds) || tradeIds.length === 0) {
        return rejectWithValue('No tradeIds provided');
      }

      const response = await apiInstance.hedgeAccounting.getJournalEntries({
        transaction_ids: tradeIds,
      });

      if (Array.isArray(response.data)) {
        return response.data;
      }

      if (response.data?.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }

      if (
        response.data &&
        typeof response.data === 'object' &&
        !Array.isArray(response.data) &&
        'trade_id' in response.data &&
        'je_data' in response.data
      ) {
        return [response.data as JournalEntry];
      }

      return [];
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch journal entries',
      });
    }
  },
);

export const fetchJournalEntryTypes = createAsyncThunk(
  'hedgeAccounting/fetchJournalEntryTypes',
  async (_, { rejectWithValue }) => {
    try {
      const api = apiInstance.hedgeAccounting;
      const response = await api.getJournalEntryTypes();
      return response.data;
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch journal entry types',
      });
    }
  },
);

export const fetchEffectivenessTypes = createAsyncThunk(
  'hedgeAccounting/fetchEffectivenessTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.hedgeAccounting.getEffectivenessType();
      return response.data;
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch effectiveness types',
      });
    }
  },
);

export const fetchTransactionAmortTypes = createAsyncThunk<
  TransactionAmortType[],
  void,
  { rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionAmortTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getTransactionAmortType();
      // API directly returns an array of TransactionAmortType objects
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch transaction amortization types',
      });
    }
  },
);

export const fetchJournalEntryMovementStandards = createAsyncThunk<
  JournalEntryMovementStandard[],
  void,
  { rejectValue: string }
>(
  'hedgeAccounting/fetchJournalEntryMovementStandards',
  async (_, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getJournalEntryMovementStandards();
      if (Array.isArray(response.data)) {
        return response.data;
      }
      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.data)
      ) {
        return response.data.data;
      }
      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch journal entry movement standards',
      });
    }
  },
);

export const fetchHedgeAmortData = createAsyncThunk<
  HedgeAmortData[],
  TradeIdsFetchPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/fetchHedgeAmortData',
  async ({ tradeIds, runDate }, { rejectWithValue }) => {
    try {
      if (!Array.isArray(tradeIds) || tradeIds.length === 0) {
        return rejectWithValue('No tradeIds provided');
      }

      const params: {
        transaction_ids: string[];
        run_date?: string;
      } = {
        transaction_ids: tradeIds,
      };

      if (runDate) {
        params.run_date = runDate;
      }

      const response =
        await apiInstance.hedgeAccounting.fetchHedgeAmortData(params);

      if (response.data?.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }

      return [];
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch hedge amortization data',
      });
    }
  },
);

export const createTransactionAmort = createAsyncThunk<
  HedgeAmortData,
  { payload: CreateTransactionAmortPayload; tempId: number },
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/createTransactionAmort',
  async ({ payload, tempId }, { rejectWithValue, dispatch }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.createTransactionAmort(payload);

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      let apiData: any;

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (
          !response.data.data ||
          Object.keys(response.data.data).length === 0
        ) {
          return rejectWithValue('Empty data returned from API');
        }
        apiData = response.data.data;
      }
      // Handle direct data response
      else if (
        typeof response.data === 'object' &&
        !('success' in response.data)
      ) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        apiData = response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      } else {
        return rejectWithValue('Invalid response format from API');
      }

      // Transform the API response to HedgeAmortData format
      const transformed: HedgeAmortData = {
        ...apiData,
        je_type_id: apiData.je_type ?? apiData.je_type_id,
        amort_schedule_transaction_id:
          apiData.amort_schedule_transaction ??
          apiData.amort_schedule_transaction_id,
        amort_schedule_type_id:
          apiData.amort_schedule_type ?? apiData.amort_schedule_type_id,
      };

      await dispatch(removeLocalAmortRow(tempId));

      dispatch(upsertLocalAmortRow({ data: transformed, isNew: true }));

      toastMessage.success('Transaction amortization created successfully');

      return transformed;
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to create transaction amortization',
      });
    }
  },
);

export const updateTransactionAmort = createAsyncThunk<
  HedgeAmortData,
  UpdateTransactionAmortPayload,
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/updateTransactionAmort',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiInstance.hedgeAccounting.updateTransactionAmort(
        payload.hedge_acc_amort_id,
        payload.data,
      );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      let apiData: any;

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (
          !response.data.data ||
          Object.keys(response.data.data).length === 0
        ) {
          return rejectWithValue('Empty data returned from API');
        }
        apiData = response.data.data;
      }
      // Handle direct data response
      else if (
        typeof response.data === 'object' &&
        !('success' in response.data)
      ) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        apiData = response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      } else {
        return rejectWithValue('Invalid response format from API');
      }

      // Transform the API response to HedgeAmortData format
      const transformed: HedgeAmortData = {
        ...apiData,
        je_type_id: apiData.je_type ?? apiData.je_type_id,
        amort_schedule_transaction_id:
          apiData.amort_schedule_transaction ??
          apiData.amort_schedule_transaction_id,
        amort_schedule_type_id:
          apiData.amort_schedule_type ?? apiData.amort_schedule_type_id,
      };

      dispatch(upsertLocalAmortRow({ data: transformed, isNew: false }));

      toastMessage.success('Transaction amortization updated successfully');

      return transformed;
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to update transaction amortization',
      });
    }
  },
);

export const patchTransactionAmort = createAsyncThunk<
  HedgeAmortData,
  PatchTransactionAmortPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/patchTransactionAmort',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiInstance.hedgeAccounting.patchTransactionAmort(
        payload.hedge_acc_amort_id,
        payload.data,
      );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (
          !response.data.data ||
          Object.keys(response.data.data).length === 0
        ) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction amortization patched successfully');
        return response.data.data;
      }
      // Handle direct data response
      else if (
        typeof response.data === 'object' &&
        !('success' in response.data)
      ) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction amortization patched successfully');
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to patch transaction amortization',
      });
    }
  },
);

export const deleteTransactionAmort = createAsyncThunk<
  number,
  number,
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/deleteTransactionAmort',
  async (hedge_acc_amort_id, { rejectWithValue, dispatch }) => {
    try {
      await apiInstance.hedgeAccounting.deleteTransactionAmort(
        hedge_acc_amort_id,
      );
      dispatch(removeLocalAmortRow(hedge_acc_amort_id));

      toastMessage.success('Transaction amortization deleted successfully');

      return hedge_acc_amort_id;
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to delete transaction amortization',
      });
    }
  },
);

export const calculateAmortEffectiveRate = createAsyncThunk<
  HedgeAmortData,
  CalculateAmortEffectiveRatePayload,
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/calculateAmortEffectiveRate',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.calculateAmortEffectiveRate(
          payload.hedge_acc_amort_id,
          payload.run_date,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      let apiData: any;

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (
          !response.data.data ||
          Object.keys(response.data.data).length === 0
        ) {
          return rejectWithValue('Empty data returned from API');
        }
        apiData = response.data.data;
      }
      // Handle direct data response
      else if (
        typeof response.data === 'object' &&
        !('success' in response.data)
      ) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        apiData = response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      } else {
        return rejectWithValue('Invalid response format from API');
      }

      // Transform the API response to HedgeAmortData format
      const transformed: HedgeAmortData = {
        ...apiData,
        je_type_id: apiData.je_type ?? apiData.je_type_id,
        amort_schedule_transaction_id:
          apiData.amort_schedule_transaction ??
          apiData.amort_schedule_transaction_id,
        amort_schedule_type_id:
          apiData.amort_schedule_type ?? apiData.amort_schedule_type_id,
      };

      toastMessage.success(
        'Amortization effective rate calculated successfully',
      );

      return transformed;
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to calculate amortization effective rate',
      });
    }
  },
);

export const fetchTransactionAmortSetSchedule = createAsyncThunk<
  TransactionAmortSetScheduleData[],
  number,
  { rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionAmortSetSchedule',
  async (hedge_acc_amort_id, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.fetchTransactionAmortSetSchedule(
          hedge_acc_amort_id,
        );

      // Handle different response formats
      if (Array.isArray(response.data)) {
        return response.data;
      }

      if (response.data && typeof response.data === 'object') {
        if (
          'success' in response.data &&
          response.data.success &&
          Array.isArray(response.data.data)
        ) {
          return response.data.data;
        }
      }

      return [];
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch transaction amortization schedule',
      });
    }
  },
);

export const createTransactionAmortSetSchedule = createAsyncThunk<
  void,
  CreateTransactionAmortSetSchedulePayload,
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/createTransactionAmortSetSchedule',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      // 1) Create the schedule entry on the server
      await apiInstance.hedgeAccounting.createTransactionAmortSetSchedule(
        payload,
      );

      // 2) Reload the schedule list for this amort ID
      dispatch(
        searchTransactionAmortSetSchedule({
          hedge_acc_trans_amort_id: payload.amort_id,
        }),
      );
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to create transaction amortization schedule',
      });
    }
  },
);

export const searchTransactionAmortSetSchedule = createAsyncThunk<
  Record<number, TransactionAmortSetScheduleData[]>,
  SearchTransactionAmortSetSchedulePayload,
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/searchTransactionAmortSetSchedule',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.searchTransactionAmortSetSchedule(
          payload,
        );

      // normalize into Record<amortId, data[]>
      let result: Record<number, TransactionAmortSetScheduleData[]> = {};
      if (
        response.data &&
        typeof response.data === 'object' &&
        response.data.success &&
        Array.isArray(response.data.data)
      ) {
        const items = response.data.data;
        const amortId = payload.hedge_acc_trans_amort_id;
        if (amortId != null) {
          result = { [amortId]: items };
        }
      } else if (
        response.data &&
        typeof response.data === 'object' &&
        !Array.isArray(response.data)
      ) {
        result = response.data as Record<
          number,
          TransactionAmortSetScheduleData[]
        >;
      } else {
        return rejectWithValue('Invalid response format from API');
      }

      // dispatch into slice
      dispatch(setTransactionAmortSchedules(result));
      return result;
    } catch (err: any) {
      return thunkError(err, rejectWithValue, {
        prefix: 'Failed to fetch amortization schedules',
      });
    }
  },
);

export const initialAmortEffectiveRate = createAsyncThunk<
  InitAmortEffectiveRateResponse,
  InitAmortEffectiveRatePayload,
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/initialAmortEffectiveRate',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.initialAmortEffectiveRate(params);

      let payload: InitAmortEffectiveRateResponse;
      if (response.data && typeof response.data === 'object') {
        if ('success' in response.data) {
          payload = response.data as InitAmortEffectiveRateResponse;
        } else {
          payload = { success: true, data: response.data };
        }
      } else {
        payload = { success: true, data: response.data };
      }

      if (payload.success && Array.isArray(payload.data)) {
        payload.data.forEach((item: HedgeAmortData) =>
          dispatch(upsertLocalAmortRow({ data: item, isNew: true })),
        );
      }

      return payload;
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to initialize amort effective rate',
      });
    }
  },
);

export const fetchTransactionEffectivenessList = createAsyncThunk<
  TransactionEffectivenessData[],
  TradeIdsFetchPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionEffectivenessList',
  async ({ tradeIds, runDate }, { rejectWithValue }) => {
    try {
      if (!Array.isArray(tradeIds) || tradeIds.length === 0) {
        return rejectWithValue('No tradeIds provided');
      }

      const params: {
        transaction_ids: string[];
        run_date?: string;
      } = {
        transaction_ids: tradeIds,
      };

      if (runDate) {
        params.run_date = runDate;
      }

      const response =
        await apiInstance.hedgeAccounting.fetchTransactionEffectivenessList(
          params,
        );

      if (Array.isArray(response.data)) {
        return response.data;
      }

      if (response.data?.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }

      return [];
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch transaction effectiveness data',
      });
    }
  },
);

export const createTransactionEffectiveness = createAsyncThunk<
  TransactionEffectivenessData,
  TransactionEffectivenessPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/createTransactionEffectiveness',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.createTransactionEffectiveness(
          payload,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (
          !response.data.data ||
          Object.keys(response.data.data).length === 0
        ) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction effectiveness created successfully');
        return response.data.data;
      }
      // Handle direct data response
      else if (
        typeof response.data === 'object' &&
        !('success' in response.data)
      ) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction effectiveness created successfully');
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to create transaction effectiveness data',
      });
    }
  },
);

export const updateTransactionEffectiveness = createAsyncThunk<
  TransactionEffectivenessData,
  UpdateTransactionEffectivenessPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/updateTransactionEffectiveness',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.updateTransactionEffectiveness(
          payload.hedge_acc_trans_eff_id,
          payload.data,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (
          !response.data.data ||
          Object.keys(response.data.data).length === 0
        ) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction effectiveness updated successfully');
        return response.data.data;
      }
      // Handle direct data response
      else if (
        typeof response.data === 'object' &&
        !('success' in response.data)
      ) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction effectiveness updated successfully');
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to update transaction effectiveness data',
      });
    }
  },
);

export const patchTransactionEffectiveness = createAsyncThunk<
  TransactionEffectivenessData,
  PatchTransactionEffectivenessPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/patchTransactionEffectiveness',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.patchTransactionEffectiveness(
          payload.hedge_acc_trans_eff_id,
          payload.data,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (
          !response.data.data ||
          Object.keys(response.data.data).length === 0
        ) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction effectiveness patched successfully');
        return response.data.data;
      }
      // Handle direct data response
      else if (
        typeof response.data === 'object' &&
        !('success' in response.data)
      ) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('Transaction effectiveness patched successfully');
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to patch transaction effectiveness data',
      });
    }
  },
);

export const deleteTransactionEffectiveness = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>(
  'hedgeAccounting/deleteTransactionEffectiveness',
  async (hedge_acc_trans_eff_id, { rejectWithValue }) => {
    try {
      await apiInstance.hedgeAccounting.deleteTransactionEffectiveness(
        hedge_acc_trans_eff_id,
      );

      toastMessage.success('Transaction effectiveness deleted successfully');

      // Return the ID that was deleted for state updates
      return hedge_acc_trans_eff_id;
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to delete transaction effectiveness data',
      });
    }
  },
);

export const fetchTransactionRelations = createAsyncThunk<
  TransactionRelation[],
  TradeIdsFetchPayload,
  { rejectValue: string; state: { hedgeAccounting: HedgeAccountingState } }
>(
  'hedgeAccounting/fetchTransactionRelations',
  async ({ tradeIds, runDate }, { rejectWithValue }) => {
    try {
      if (!Array.isArray(tradeIds) || tradeIds.length === 0) {
        return rejectWithValue('No tradeIds provided');
      }

      const params: {
        transaction_ids: string[];
        run_date?: string;
      } = {
        transaction_ids: tradeIds,
      };

      if (runDate) {
        params.run_date = runDate;
      }

      const response =
        await apiInstance.hedgeAccounting.fetchTransactionRelations(params);

      if (Array.isArray(response.data)) {
        return response.data;
      }

      if (response.data?.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }

      return [];
    } catch (error: any) {
      // preserve your prior special handling
      if (error?.message && String(error.message).includes('NoneType')) {
        return [];
      }

      if (error?.response?.status === 400) {
        return [];
      }

      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch transaction relations data',
      });
    }
  },
);

export const fetchTransactionsWithoutHedgeAcc = createAsyncThunk<
  TransactionWithoutHedgeAcc[],
  TradeIdsFetchPayload,
  { rejectValue: string; state: { hedgeAccounting: HedgeAccountingState } }
>(
  'hedgeAccounting/fetchTransactionsWithoutHedgeAcc',
  async ({ tradeIds, runDate }, { rejectWithValue }) => {
    try {
      if (!Array.isArray(tradeIds) || tradeIds.length === 0) {
        return rejectWithValue('No tradeIds provided');
      }

      const params: {
        transaction_ids: string[];
        run_date?: string;
      } = {
        transaction_ids: tradeIds,
      };

      if (runDate) {
        params.run_date = runDate;
      }

      const response =
        await apiInstance.hedgeAccounting.fetchTransactionsWithoutHedgeAcc(
          params,
        );

      if (Array.isArray(response.data)) {
        return response.data;
      }

      if (response.data?.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }

      return [];
    } catch (error: any) {
      // preserve your prior 400 handling
      if (error?.response?.status === 400) {
        return [];
      }

      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch transactions without hedge accounting data',
      });
    }
  },
);

export const createTransactionNoHedgeAcc = createAsyncThunk<
  TransactionWithoutHedgeAcc,
  CreateTransactionNoHedgeAccPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/createTransactionNoHedgeAcc',
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.createTransactionNoHedgeAcc(params);

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (
          !response.data.data ||
          Object.keys(response.data.data).length === 0
        ) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('No hedge accounting record created successfully');
        return response.data.data;
      }
      // Handle direct data response
      else if (
        typeof response.data === 'object' &&
        !('success' in response.data)
      ) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('No hedge accounting record created successfully');
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      console.error('Create transaction no hedge acc API Error:', error);

      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to create no hedge accounting record',
      });
    }
  },
);

export const updateTransactionNoHedgeAcc = createAsyncThunk<
  TransactionWithoutHedgeAcc,
  UpdateTransactionNoHedgeAccPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/updateTransactionNoHedgeAcc',
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.updateTransactionNoHedgeAcc(params);

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (
          !response.data.data ||
          Object.keys(response.data.data).length === 0
        ) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('No hedge accounting record updated successfully');
        return response.data.data;
      }
      // Handle direct data response
      else if (
        typeof response.data === 'object' &&
        !('success' in response.data)
      ) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        toastMessage.success('No hedge accounting record updated successfully');
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      console.error('Update transaction no hedge acc API Error:', error);

      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to update no hedge accounting record',
      });
    }
  },
);

export const fetchTransactionTitle = createAsyncThunk<
  TransactionTitle,
  TransactionTitlePayload,
  { rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionTitle',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiInstance.hedgeAccounting.getTransactionTitle(
        payload.transactionId,
      );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (
          !response.data.data ||
          Object.keys(response.data.data).length === 0
        ) {
          return rejectWithValue('Empty data returned from API');
        }
        return response.data.data;
      }
      // Handle direct data response
      else if (
        typeof response.data === 'object' &&
        !('success' in response.data)
      ) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch transaction title',
      });
    }
  },
);

export const fetchTransactionLegs = createAsyncThunk<
  { transactionId: number; legs: TransactionLeg[] },
  { transactionId: number },
  { rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionLegs',
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.fetchTransactionLegs(transactionId);

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data/legs structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data &&
        response.data.data &&
        'legs' in response.data.data &&
        Array.isArray(response.data.data.legs)
      ) {
        return {
          transactionId,
          legs: response.data.data.legs,
        };
      }
      // Handle direct data/legs response
      else if (
        typeof response.data === 'object' &&
        'data' in response.data &&
        response.data.data &&
        'legs' in response.data.data &&
        Array.isArray(response.data.data.legs)
      ) {
        return {
          transactionId,
          legs: response.data.data.legs,
        };
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch transaction legs',
      });
    }
  },
);

export const fetchBulkTransactionTitles = createAsyncThunk<
  BulkTransactionTitlesResponse,
  BulkTransactionTitlesRequest,
  { rejectValue: string }
>(
  'hedgeAccounting/fetchBulkTransactionTitles',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getBulkTransactionTitles(payload);

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch bulk transaction titles',
      });
    }
  },
);

export const fetchTransactionDetail = createAsyncThunk<
  BulkTransactionTitle,
  { transactionId: number },
  { rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionDetail',
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getTransactionDetail(transactionId);

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch transaction detail',
      });
    }
  },
);

export const fetchTransactionTitleStructured = createAsyncThunk<
  BulkTransactionTitle,
  { transactionId: number },
  { rejectValue: string }
>(
  'hedgeAccounting/fetchTransactionTitleStructured',
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getTransactionTitleStructured(
          transactionId,
        );

      if (response.data?.success && response.data?.data) {
        // Ensure transaction_id is included in the response
        return {
          ...response.data.data,
          transaction_id: transactionId,
        };
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch transaction title structured',
      });
    }
  },
);

export const fetchBulkTransactionTitlesStructured = createAsyncThunk<
  BulkTransactionTitle[],
  { transaction_ids: number[] },
  { rejectValue: string }
>(
  'hedgeAccounting/fetchBulkTransactionTitlesStructured',
  async ({ transaction_ids }, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.postBulkTransactionTitlesStructured({
          transaction_ids,
        });

      if (response.data?.success && response.data?.data?.transaction_titles) {
        return response.data.data.transaction_titles;
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch bulk transaction titles structured',
      });
    }
  },
);

export const createHypoTrade = createAsyncThunk<
  any,
  {
    transaction_id: number;
    delete_floor_leg?: boolean;
    create_floor_leg?: boolean;
    strike?: number;
    coupon?: number;
  },
  { rejectValue: string }
>('hedgeAccounting/createHypoTrade', async (params, { rejectWithValue }) => {
  try {
    const response = await apiInstance.hedgeAccounting.createHypoTrade(params);

    // Handle empty response (200 but no data)
    if (!response.data) {
      return rejectWithValue('Empty response from API');
    }

    // Handle success/data structure
    if (
      typeof response.data === 'object' &&
      'success' in response.data &&
      response.data.success === true &&
      'data' in response.data
    ) {
      toastMessage.success('Hypothetical trade created successfully');
      return response.data.data || response.data;
    }
    // Handle direct data response
    else if (typeof response.data === 'object') {
      // Handle success: false case
      if ('success' in response.data && response.data.success === false) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      }
      toastMessage.success('Hypothetical trade created successfully');
      return response.data;
    }

    return rejectWithValue('Invalid response format from API');
  } catch (error: any) {
    return thunkError(error, rejectWithValue, {
      prefix: 'Failed to create hypothetical trade',
    });
  }
});

export const getTradeDesignationDate = createAsyncThunk<
  TradeDesignationDate,
  number,
  { rejectValue: string }
>(
  'hedgeAccounting/getTradeDesignationDate',
  async (transactionId, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getTradeDesignationDate(
          transactionId,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (
          !response.data.data ||
          Object.keys(response.data.data).length === 0
        ) {
          return rejectWithValue('Empty data returned from API');
        }
        return response.data.data;
      }
      // Handle direct data response
      else if (
        typeof response.data === 'object' &&
        !('success' in response.data)
      ) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch trade designation date',
      });
    }
  },
);

export const postTradeDesignationDate = createAsyncThunk<
  { success: boolean; message: string },
  { transaction_id: number; designation_date: string },
  { rejectValue: string }
>(
  'hedgeAccounting/postTradeDesignationDate',
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.postTradeDesignationDate(params);

      // Handle empty response (200 but no data)
      if (!response.data) {
        // Empty response can be treated as success for POST operations
        toastMessage.success('Trade designation date set successfully');
        return {
          success: true,
          message: 'Trade designation date set successfully',
        };
      }

      // Handle success/data structure
      if (typeof response.data === 'object' && 'success' in response.data) {
        // Handle success: false case
        if (response.data.success === false) {
          return rejectWithValue(
            response.data.error_msg || 'API returned success: false',
          );
        }
        toastMessage.success('Trade designation date set successfully');
        return {
          success: response.data.success,
          message:
            response.data.data || 'Trade designation date set successfully',
        };
      }
      // Handle direct response
      else if (typeof response.data === 'object') {
        toastMessage.success('Trade designation date set successfully');
        return {
          success: true,
          message: 'Trade designation date set successfully',
        };
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to set trade designation date',
      });
    }
  },
);

export const deleteTradeDesignationDate = createAsyncThunk<
  { success: boolean; message: string },
  number,
  { rejectValue: string }
>(
  'hedgeAccounting/deleteTradeDesignationDate',
  async (transactionId, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.deleteTradeDesignationDate(
          transactionId,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        // Empty response can be treated as success for DELETE operations
        toastMessage.success('Trade designation date deleted successfully');
        return {
          success: true,
          message: 'Trade designation date deleted successfully',
        };
      }

      // Handle success/data structure
      if (typeof response.data === 'object' && 'success' in response.data) {
        // Handle success: false case
        if (response.data.success === false) {
          return rejectWithValue(
            response.data.error_msg || 'API returned success: false',
          );
        }
        toastMessage.success('Trade designation date deleted successfully');
        return {
          success: response.data.success,
          message:
            response.data.data || 'Trade designation date deleted successfully',
        };
      }
      // Handle direct response
      else if (typeof response.data === 'object') {
        toastMessage.success('Trade designation date deleted successfully');
        return {
          success: true,
          message: 'Trade designation date deleted successfully',
        };
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to delete trade designation date',
      });
    }
  },
);

export const getIneffectivenessValues = createAsyncThunk(
  'hedgeAccounting/getIneffectivenessValues',
  async (transactionId: number, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getIneffectivenessValues(
          transactionId,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (
          !response.data.data ||
          Object.keys(response.data.data).length === 0
        ) {
          return rejectWithValue('Empty data returned from API');
        }
        return response.data.data;
      }
      // Handle direct data response
      else if (
        typeof response.data === 'object' &&
        !('success' in response.data)
      ) {
        // Handle empty object
        if (Object.keys(response.data).length === 0) {
          return rejectWithValue('Empty data returned from API');
        }
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      const msg = extractErrorMessage(error);
      const finalMsg = `Failed to fetch ineffectiveness values: ${msg}`;
      toastMessage.warning(finalMsg);
      return rejectWithValue(finalMsg);
    }
  },
);

export const postIneffectivenessValues = createAsyncThunk(
  'hedgeAccounting/postIneffectivenessValues',
  async (
    {
      transactionId,
      ineffectivenessValue,
      hypoValue,
    }: {
      transactionId: number;
      ineffectivenessValue?: number | null;
      hypoValue?: number | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.postIneffectivenessValues(
          transactionId,
          ineffectivenessValue,
          hypoValue,
        );

      // Handle empty response (200 but no data)
      if (!response || !response.data) {
        // Check if response object has success property directly
        if (response && 'success' in response && response.success === true) {
          toastMessage.success('Ineffectiveness values updated.');
          return {};
        }
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true
      ) {
        toastMessage.success('Ineffectiveness values updated.');
        return response.data.data || response.data;
      }
      // Handle direct success on response object
      else if ('success' in response && response.success === true) {
        toastMessage.success('Ineffectiveness values updated.');
        return response.data;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      }
      // Handle direct data response
      else if (typeof response.data === 'object') {
        toastMessage.success('Ineffectiveness values updated.');
        return response.data;
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      if (
        error.response?.data?.error_msg === 'error related to trade_id fields'
      ) {
        toastMessage.warning(
          'Hypothetical transaction not found. Please create a hypothetical transaction and try again.',
        );
      }
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to post ineffectiveness values',
      });
    }
  },
);

export const fetchJournalEntryMovements = createAsyncThunk<
  JournalEntryMovement[],
  number,
  { rejectValue: string }
>(
  'hedgeAccounting/fetchJournalEntryMovements',
  async (transactionId, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getJournalEntryMovements(
          transactionId,
        );

      // The API returns an array of journal entry movements directly
      if (Array.isArray(response.data)) {
        return response.data;
      }

      // Handle nested success/data structure if present
      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.data)
      ) {
        return response.data.data;
      }

      return [];
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to fetch journal entry movements',
      });
    }
  },
);

export const createJournalEntryMovements = createAsyncThunk<
  string,
  CreateJournalEntryMovementsPayload,
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/createJournalEntryMovements',
  async ({ transactionId, standardId }, { rejectWithValue, dispatch }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.createJournalEntryMovements({
          transaction_ids: [transactionId],
          standard_id: standardId,
        });

      // Handle empty response (200 but no data) - treat as success for POST
      if (!response.data) {
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true
      ) {
        // Handle success: false case
        if (response.data.success === false) {
          const message =
            response.data.error_msg || 'API returned success: false';
          toastMessage.error(
            `Failed to create journal entry movement: ${message}`,
          );
          return rejectWithValue(message);
        }
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }
      // If it's directly a string
      else if (typeof response.data === 'string') {
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }
      // Handle direct data response
      else if (typeof response.data === 'object') {
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(`Failed to create journal entry movement: ${message}`);
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to create journal entry movements',
      });
    }
  },
);

export const editJournalEntryMovementComment = createAsyncThunk(
  'hedgeAccounting/editJournalEntryMovementComment',
  async (
    {
      transactionId,
      jeMovementId,
      comment,
    }: EditJournalEntryMovementCommentPayload,
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.editJournalEntryMovementComment(
          transactionId,
          jeMovementId,
          comment,
        );

      // Handle empty response (200 but no data) - treat as success for PUT/PATCH
      if (!response.data) {
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }

      // Handle success/data structure
      if (typeof response.data === 'object' && 'success' in response.data) {
        // Handle success: false case
        if (response.data.success === false) {
          return rejectWithValue(
            response.data.error_msg || 'API returned success: false',
          );
        }
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }
      // Handle string response
      else if (
        response.data === 'OK' ||
        (typeof response.data === 'string' && response.data.includes('OK'))
      ) {
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }
      // Handle any other response as success
      else if (response.data) {
        dispatch(fetchJournalEntryMovements(transactionId));
        return 'OK';
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      console.error('Error editing journal entry movement comment:', error);
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to edit journal entry movement comment',
      });
    }
  },
);

export const getCompanyEntityHedgingStandard = createAsyncThunk<
  CompanyEntityHedgingStandard,
  string,
  { rejectValue: string }
>(
  'hedgeAccounting/getCompanyEntityHedgingStandard',
  async (companyEntityId, { rejectWithValue }) => {
    const notDefinedMessage = `Hedge accounting standard for company entity with ID '${companyEntityId}' not defined.`;

    try {
      const response =
        await apiInstance.hedgeAccounting.getCompanyEntityHedgingStandard(
          companyEntityId,
        );

      // Handle empty response (200 but no data)
      if (!response.data) {
        toastMessage.warning(notDefinedMessage);
        return rejectWithValue(notDefinedMessage);
      }

      // Handle success response
      if (typeof response.data === 'object') {
        // Handle success: false case
        if ('success' in response.data && response.data.success === false) {
          const message = response.data.error_msg || notDefinedMessage;
          toastMessage.warning(message);
          return rejectWithValue(message);
        }

        // If it has success/data structure, return the data
        if (
          'success' in response.data &&
          response.data.success === true &&
          response.data.data
        ) {
          // Handle empty data object
          if (Object.keys(response.data.data).length === 0) {
            toastMessage.warning(notDefinedMessage);
            return rejectWithValue(notDefinedMessage);
          }
          return response.data.data;
        }

        // If it's already a CompanyEntityHedgingStandard, return it directly
        if ('hedge_acc_standard' in response.data) {
          return response.data as CompanyEntityHedgingStandard;
        }
      }

      toastMessage.warning(notDefinedMessage);
      return rejectWithValue(notDefinedMessage);
    } catch (error: any) {
      const message = error?.response?.data?.error_msg || notDefinedMessage;
      toastMessage.warning(message);
      return rejectWithValue(notDefinedMessage);
    }
  },
);

export const createJournalEntries = createAsyncThunk<
  JournalEntry[],
  JournalEntryCommand[],
  { rejectValue: string }
>(
  'hedgeAccounting/createJournalEntries',
  async (commands, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.createOrUpdateJournalEntries(
          commands,
        );

      // Handle different response formats
      if (Array.isArray(response.data)) {
        return response.data;
      }

      // Handle nested success/data structure if present
      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.data)
      ) {
        return response.data.data;
      }

      // If response structure is unexpected, return empty array and log warning
      console.warn(
        'Unexpected response format from createJournalEntries:',
        response.data,
      );
      return [];
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to create journal entries',
      });
    }
  },
);

// Create and Reset Journal Entries - combines create with selection reset
export const createAndResetJournalEntries = createAsyncThunk(
  'hedgeAccounting/createAndResetJournalEntries',
  async (
    payload: {
      tradeId: number;
      jeDate: string | null;
      selectedTypes: { label: string; value: number }[];
    },
    { dispatch, rejectWithValue, getState },
  ) => {
    try {
      if (!payload.tradeId) {
        throw new Error('Cannot add journal entries: No trade ID available');
      }

      // Create the commands array with the selected journal entry types
      const commands = [
        {
          row_status: 'new', // new, update, delete
          trade_id: Number(payload.tradeId),
          je_date: payload.jeDate,
          je_status_id: 2, // Default status ID
          je_comment: '',
          entries: payload.selectedTypes.map((type) => ({
            je_type: type.label,
            je_amount: 0, // Default amount
          })),
        },
      ];

      // Create journal entries using the existing thunk
      await dispatch(createJournalEntries(commands)).unwrap();

      // Fetch updated journal entries for this trade
      await (dispatch as any)(
        fetchJournalEntries({ tradeIds: [String(payload.tradeId)] }),
      ).unwrap();

      // Get the latest state after fetching
      const state = getState() as { hedgeAccounting: HedgeAccountingState };

      return {
        journalEntries: state.hedgeAccounting.journalEntries,
      };
    } catch (error: any) {
      console.error('Failed to create journal entries:', error);
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to create journal entries',
      });
    }
  },
);

export const deleteTransactionAmortSetSchedule = createAsyncThunk<
  void,
  { scheduleId: number; amortId: number },
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/deleteTransactionAmortSetSchedule',
  async ({ scheduleId, amortId }, { rejectWithValue, dispatch }) => {
    try {
      // 1) Delete the schedule entry
      await apiInstance.hedgeAccounting.deleteTransactionAmortSetSchedule(
        scheduleId,
      );
      // 2) Reload remaining schedules for this amortization
      dispatch(
        searchTransactionAmortSetSchedule({
          hedge_acc_trans_amort_id: amortId,
        }),
      );
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to delete transaction amortization schedule',
      });
    }
  },
);

export const updateTransactionAmortSetSchedule = createAsyncThunk<
  void,
  {
    scheduleId: number;
    amortId: number;
    data: {
      hedge_acc_trans_amort_id: number;
      period_start_date: string;
      period_end_date: string;
      trans_amort_amount: number;
    };
  },
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/updateTransactionAmortSetSchedule',
  async ({ scheduleId, amortId, data }, { rejectWithValue, dispatch }) => {
    try {
      await apiInstance.hedgeAccounting.updateTransactionAmortSetSchedule(
        scheduleId,
        data,
      );

      // 2) Reload all schedules for that amort ID
      dispatch(
        searchTransactionAmortSetSchedule({
          hedge_acc_trans_amort_id: amortId,
        }),
      );
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to update transaction amortization schedule',
      });
    }
  },
);

export const createReplicaHypothetical = createAsyncThunk<
  void,
  { transaction_id: string },
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/createReplicaHypothetical',
  async ({ transaction_id }, { rejectWithValue, dispatch }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.createReplicaHypothetical(
          transaction_id,
        );

      if (
        response?.data &&
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'Failed to create hypothetical replica',
        );
      }

      toastMessage.success('Hypothetical replica created successfully');

      dispatch(
        fetchTransactionRelations({ tradeIds: [String(transaction_id)] }),
      );
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to create hypothetical replica',
      });
    }
  },
);

export const deleteHypotheticalTrade = createAsyncThunk<
  void,
  {
    transaction_relationship_id: string;
    child_transaction_id: number;
    parent_transaction_id: number;
  },
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/deleteHypotheticalTrade',
  async (
    {
      transaction_relationship_id,
      child_transaction_id,
      parent_transaction_id,
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await apiInstance.hedgeAccounting.deleteHypotheticalTransaction(
        transaction_relationship_id,
      );

      await apiInstance.hedgeAccounting.clearTransactionRegression(
        child_transaction_id,
      );

      dispatch(
        fetchTransactionRelations({
          tradeIds: [String(parent_transaction_id)],
        }),
      );
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to delete hypothetical trade',
      });
    }
  },
);

export const onClearTransactionRegression = createAsyncThunk<
  void,
  { parent_transaction_id: number; child_transaction_id: number },
  { rejectValue: string; dispatch: AppDispatch }
>(
  'hedgeAccounting/clearRegressionValues',
  async (
    { parent_transaction_id, child_transaction_id },
    { rejectWithValue, dispatch },
  ) => {
    toastMessage.info('Clearing Regression Values');
    try {
      const parentResp =
        await apiInstance.hedgeAccounting.clearTransactionRegression(
          parent_transaction_id,
        );

      const childResp =
        await apiInstance.hedgeAccounting.clearTransactionRegression(
          child_transaction_id,
        );

      if (
        (parentResp.data?.success === true ||
          parentResp.data?.data === 'success') &&
        (childResp.data?.success === true || childResp.data?.data === 'success')
      ) {
        toastMessage.success(
          'Regression Values cleared successfully for both transactions',
        );
      }

      dispatch(
        fetchTransactionRelations({
          tradeIds: [String(parent_transaction_id)],
        }),
      );
    } catch (error: any) {
      toastMessage.error('Failed to clear regression values');
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to clear regression values',
      });
    }
  },
);

export const updateTransactionFields = createAsyncThunk<
  BulkTransactionTitle,
  UpdateTransactionFieldsPayload,
  { rejectValue: string }
>(
  'hedgeAccounting/updateTransactionFields',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.updateTransactionFields(payload);

      // Handle empty response (200 but no data)
      if (!response.data) {
        return rejectWithValue('Empty response from API');
      }

      // Handle success/data structure
      if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === true &&
        'data' in response.data
      ) {
        // Handle empty data object
        if (
          !response.data.data ||
          Object.keys(response.data.data).length === 0
        ) {
          return rejectWithValue('Empty data returned from API');
        }

        const transactionData = response.data.data.transaction_data;
        toastMessage.success('Transaction fields updated successfully');
        return transactionData;
      }
      // Handle success: false case
      else if (
        typeof response.data === 'object' &&
        'success' in response.data &&
        response.data.success === false
      ) {
        return rejectWithValue(
          response.data.error_msg || 'API returned success: false',
        );
      }

      return rejectWithValue('Invalid response format from API');
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to update transaction fields',
      });
    }
  },
);

export const bulkReplaceJournalEntriesFromReference = createAsyncThunk<
  { updatedTradeIds: string[] },
  {
    referenceTrade: {
      tradeId: string;
      journalEntries?: JournalEntry[];
    };
    targetTradeIds: string[];
  },
  {
    rejectValue: string;
    dispatch: AppDispatch;
  }
>(
  'hedgeAccounting/bulkReplaceJournalEntriesFromReference',
  async ({ referenceTrade, targetTradeIds }, { rejectWithValue, dispatch }) => {
    try {
      if (!referenceTrade?.tradeId) {
        return rejectWithValue('Reference trade is required');
      }

      const filteredTargetTradeIds = targetTradeIds.filter(
        (targetTradeId) =>
          String(targetTradeId) !== String(referenceTrade.tradeId),
      );

      if (!filteredTargetTradeIds.length) {
        return rejectWithValue('Please select at least one target trade');
      }

      const referenceJeConfig = getSelectedTypesFromReferenceJeData(
        referenceTrade.journalEntries,
      );

      if (!referenceJeConfig) {
        return rejectWithValue(
          'Reference trade has no valid journal entries to replicate',
        );
      }

      for (const targetTradeId of filteredTargetTradeIds) {
        const numericTargetTradeId = Number(targetTradeId);

        if (!numericTargetTradeId) {
          continue;
        }

        await dispatch(
          createAndResetJournalEntries({
            tradeId: numericTargetTradeId,
            jeDate: referenceJeConfig.jeDate,
            selectedTypes: referenceJeConfig.selectedTypes,
          }),
        ).unwrap();
      }

      await dispatch(
        fetchJournalEntries({
          tradeIds: [String(referenceTrade.tradeId)],
        }),
      );

      toastMessage.success('Journal entries replaced successfully');

      return {
        updatedTradeIds: filteredTargetTradeIds,
      };
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to replace journal entries in bulk',
      });
    }
  },
);

export const bulkReplaceJournalEntryMovementsFromReference = createAsyncThunk<
  { updatedTradeIds: string[] },
  {
    referenceTrade: {
      tradeId: string;
      journalEntryMovementStandardId?: number | null;
    };
    targetTradeIds: string[];
  },
  {
    rejectValue: string;
    dispatch: AppDispatch;
  }
>(
  'hedgeAccounting/bulkReplaceJournalEntryMovementsFromReference',
  async ({ referenceTrade, targetTradeIds }, { rejectWithValue, dispatch }) => {
    try {
      if (!referenceTrade?.tradeId) {
        return rejectWithValue('Reference trade is required');
      }

      if (!referenceTrade.journalEntryMovementStandardId) {
        return rejectWithValue(
          'Reference trade has no journal entry movement logic to replicate',
        );
      }

      const filteredTargetTradeIds = targetTradeIds.filter(
        (targetTradeId) =>
          String(targetTradeId) !== String(referenceTrade.tradeId),
      );

      if (!filteredTargetTradeIds.length) {
        return rejectWithValue('Please select at least one target trade');
      }

      for (const targetTradeId of filteredTargetTradeIds) {
        const numericTargetTradeId = Number(targetTradeId);

        if (!numericTargetTradeId) {
          continue;
        }

        await dispatch(
          createJournalEntryMovements({
            transactionId: numericTargetTradeId,
            standardId: Number(referenceTrade.journalEntryMovementStandardId),
          }),
        ).unwrap();
      }

      await dispatch(
        fetchJournalEntryMovements(Number(referenceTrade.tradeId)),
      );

      toastMessage.success('Journal entry movements replaced successfully');

      return {
        updatedTradeIds: filteredTargetTradeIds,
      };
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to replace journal entry movements in bulk',
      });
    }
  },
);

export const bulkReplaceEffectivenessFromReference = createAsyncThunk<
  { updatedTradeIds: string[] },
  {
    referenceTrade: {
      tradeId: string;
      transactionEffectivenessData?: TransactionEffectivenessData[];
    };
    targetTradeIds: string[];
    runDate?: string;
  },
  {
    rejectValue: string;
    dispatch: AppDispatch;
  }
>(
  'hedgeAccounting/bulkReplaceEffectivenessFromReference',
  async (
    { referenceTrade, targetTradeIds, runDate },
    { rejectWithValue, dispatch },
  ) => {
    try {
      if (!referenceTrade?.tradeId) {
        return rejectWithValue('Reference trade is required');
      }

      const filteredTargetTradeIds = targetTradeIds.filter(
        (targetTradeId) =>
          String(targetTradeId) !== String(referenceTrade.tradeId),
      );

      if (!filteredTargetTradeIds.length) {
        return rejectWithValue('Please select at least one target trade');
      }

      const referenceEffectiveness =
        referenceTrade.transactionEffectivenessData ?? [];

      if (!hasMeaningfulEffectivenessData(referenceEffectiveness)) {
        return rejectWithValue('Reference trade has no effectiveness data');
      }

      const params: {
        transaction_ids: string[];
        run_date?: string;
      } = {
        transaction_ids: filteredTargetTradeIds,
      };

      if (runDate) {
        params.run_date = runDate;
      }

      const effectivenessResponse =
        await apiInstance.hedgeAccounting.fetchTransactionEffectivenessList(
          params,
        );

      let fetchedEffectiveness: TransactionEffectivenessData[] = [];

      if (Array.isArray(effectivenessResponse.data)) {
        fetchedEffectiveness = effectivenessResponse.data;
      } else if (
        effectivenessResponse.data?.success &&
        Array.isArray(effectivenessResponse.data.data)
      ) {
        fetchedEffectiveness = effectivenessResponse.data.data;
      }

      const localEffectivenessByTradeId =
        groupEffectivenessByTradeId(fetchedEffectiveness);

      for (const targetTradeId of filteredTargetTradeIds) {
        const numericTargetTradeId = Number(targetTradeId);

        if (!numericTargetTradeId) {
          continue;
        }

        const existingEffectiveness =
          localEffectivenessByTradeId[targetTradeId] ?? [];

        if (hasMeaningfulEffectivenessData(existingEffectiveness)) {
          for (const existingRow of existingEffectiveness) {
            const deleteResponse =
              await apiInstance.hedgeAccounting.deleteTransactionEffectiveness(
                existingRow.hedge_acc_trans_eff_id,
              );

            if (
              deleteResponse?.data &&
              typeof deleteResponse.data === 'object' &&
              'success' in deleteResponse.data &&
              deleteResponse.data.success === false
            ) {
              return rejectWithValue(
                deleteResponse.data.error_msg ||
                  `Failed to delete existing effectiveness data for trade ${targetTradeId}`,
              );
            }
          }
        }

        const createPayloads = buildCreatePayloadsFromReferenceEffectiveness(
          numericTargetTradeId,
          referenceEffectiveness,
        );

        for (const payload of createPayloads) {
          const createResponse =
            await apiInstance.hedgeAccounting.createTransactionEffectiveness(
              payload,
            );

          if (!createResponse.data) {
            return rejectWithValue(
              `Empty response while creating effectiveness data for trade ${targetTradeId}`,
            );
          }

          if (
            typeof createResponse.data === 'object' &&
            'success' in createResponse.data &&
            createResponse.data.success === false
          ) {
            return rejectWithValue(
              createResponse.data.error_msg ||
                `Failed to create effectiveness data for trade ${targetTradeId}`,
            );
          }
        }
      }

      await dispatch(
        fetchTransactionEffectivenessList({
          tradeIds: [referenceTrade.tradeId],
          ...(runDate ? { runDate } : {}),
        }),
      ).unwrap();

      toastMessage.success('Transaction effectiveness replaced successfully');

      return {
        updatedTradeIds: filteredTargetTradeIds,
      };
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to replace transaction effectiveness in bulk',
      });
    }
  },
);

export const bulkReplaceHypotheticalFromReference = createAsyncThunk<
  { updatedTradeIds: string[] },
  {
    referenceTrade: {
      tradeId: string;
      hypotheticalData?: {
        id: string;
        parentTransaction: number;
        childTransaction: number;
        relationshipType: string;
        actualIneffectiveInitialValue: string;
        hypoIneffectiveInitialValue: string;
      }[];
    };
    targetTradeIds: string[];
    runDate?: string;
  },
  {
    rejectValue: string;
    dispatch: AppDispatch;
  }
>(
  'hedgeAccounting/bulkReplaceHypotheticalFromReference',
  async (
    { referenceTrade, targetTradeIds, runDate },
    { rejectWithValue, dispatch },
  ) => {
    try {
      if (!referenceTrade?.tradeId) {
        return rejectWithValue('Reference trade is required');
      }

      const filteredTargetTradeIds = targetTradeIds.filter(
        (targetTradeId) =>
          String(targetTradeId) !== String(referenceTrade.tradeId),
      );

      if (!filteredTargetTradeIds.length) {
        return rejectWithValue('Please select at least one target trade');
      }

      if (
        !Array.isArray(referenceTrade.hypotheticalData) ||
        referenceTrade.hypotheticalData.length === 0
      ) {
        return rejectWithValue(
          'Reference trade has no hypothetical data to replicate',
        );
      }

      const params: {
        transaction_ids: string[];
        run_date?: string;
      } = {
        transaction_ids: filteredTargetTradeIds,
      };

      if (runDate) {
        params.run_date = runDate;
      }

      const relationsResponse =
        await apiInstance.hedgeAccounting.fetchTransactionRelations(params);

      let fetchedRelations: TransactionRelation[] = [];

      if (Array.isArray(relationsResponse.data)) {
        fetchedRelations = relationsResponse.data;
      } else if (
        relationsResponse.data?.success &&
        Array.isArray(relationsResponse.data.data)
      ) {
        fetchedRelations = relationsResponse.data.data;
      }

      const localRelationsByTradeId = groupRelationsByTradeId(fetchedRelations);

      for (const targetTradeId of filteredTargetTradeIds) {
        const numericTargetTradeId = Number(targetTradeId);

        if (!numericTargetTradeId) {
          continue;
        }

        const existingRelation = localRelationsByTradeId[targetTradeId] ?? null;

        if (hasMeaningfulHypotheticalData(existingRelation)) {
          const existingRows =
            getHypotheticalRowsFromRelation(existingRelation);

          for (const row of existingRows) {
            await apiInstance.hedgeAccounting.deleteHypotheticalTransaction(
              String(row.transaction_relationship_id),
            );

            await apiInstance.hedgeAccounting.clearTransactionRegression(
              row.child_transaction,
            );
          }
        }

        const createResponse =
          await apiInstance.hedgeAccounting.createReplicaHypothetical(
            String(targetTradeId),
          );

        if (
          createResponse?.data &&
          typeof createResponse.data === 'object' &&
          'success' in createResponse.data &&
          createResponse.data.success === false
        ) {
          return rejectWithValue(
            createResponse.data.error_msg ||
              `Failed to create hypothetical replica for trade ${targetTradeId}`,
          );
        }
      }

      await dispatch(
        fetchTransactionRelations({
          tradeIds: [referenceTrade.tradeId],
          ...(runDate ? { runDate } : {}),
        }),
      );

      toastMessage.success('Hypothetical transactions replaced successfully');

      return {
        updatedTradeIds: filteredTargetTradeIds,
      };
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to replace hypothetical transactions in bulk',
      });
    }
  },
);

export const bulkReplaceAmortizationFromReference = createAsyncThunk<
  { updatedTradeIds: string[] },
  {
    referenceTrade: {
      tradeId: string;
      hedgeAmortData?: HedgeAmortData[];
    };
    targetTradeIds: string[];
    runDate?: string;
  },
  {
    rejectValue: string;
    dispatch: AppDispatch;
  }
>(
  'hedgeAccounting/bulkReplaceAmortizationFromReference',
  async (
    { referenceTrade, targetTradeIds, runDate },
    { rejectWithValue, dispatch },
  ) => {
    try {
      if (!referenceTrade?.tradeId) {
        return rejectWithValue('Reference trade is required');
      }

      const filteredTargetTradeIds = targetTradeIds.filter(
        (targetTradeId) =>
          String(targetTradeId) !== String(referenceTrade.tradeId),
      );

      if (!filteredTargetTradeIds.length) {
        return rejectWithValue('Please select at least one target trade');
      }

      const referenceAmortData = referenceTrade.hedgeAmortData ?? [];

      if (!referenceAmortData.length) {
        return rejectWithValue('Reference trade has no amortization data');
      }

      const params: {
        transaction_ids: string[];
        run_date?: string;
      } = {
        transaction_ids: filteredTargetTradeIds,
      };

      if (runDate) {
        params.run_date = runDate;
      }

      const amortResponse =
        await apiInstance.hedgeAccounting.fetchHedgeAmortData(params);

      let fetchedAmortRows: HedgeAmortData[] = [];

      if (
        amortResponse.data?.success &&
        Array.isArray(amortResponse.data.data)
      ) {
        fetchedAmortRows = amortResponse.data.data;
      }

      const localAmortByTradeId = fetchedAmortRows.reduce<
        Record<string, HedgeAmortData[]>
      >((acc, item) => {
        const key = String(item.transaction_id);
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {});

      for (const targetTradeId of filteredTargetTradeIds) {
        const numericTargetTradeId = Number(targetTradeId);

        if (!numericTargetTradeId) {
          continue;
        }

        const existingAmortRows = localAmortByTradeId[targetTradeId] ?? [];

        if (existingAmortRows.length > 0) {
          for (const existingRow of existingAmortRows) {
            await apiInstance.hedgeAccounting.deleteTransactionAmort(
              existingRow.hedge_acc_amort_id,
            );
          }
        }

        for (const ref of referenceAmortData) {
          const payload: CreateTransactionAmortPayload = {
            transaction: numericTargetTradeId,
            amort_date: ref.amort_date,
            je_type: ref.je_type_id,
            amort_amt: ref.amort_amt,
            amort_eff_rate: ref.amort_eff_rate,
            amort_comment: ref.amort_comment ?? '',
            amort_schedule_transaction: numericTargetTradeId,
            amort_schedule_type: ref.amort_schedule_type_id,
          };

          const createResponse =
            await apiInstance.hedgeAccounting.createTransactionAmort(payload);

          if (!createResponse.data) {
            return rejectWithValue(
              `Empty response while creating amortization data for trade ${targetTradeId}`,
            );
          }

          if (
            typeof createResponse.data === 'object' &&
            'success' in createResponse.data &&
            createResponse.data.success === false
          ) {
            return rejectWithValue(
              createResponse.data.error_msg ||
                `Failed to create amortization data for trade ${targetTradeId}`,
            );
          }
        }
      }

      await dispatch(
        fetchHedgeAmortData({
          tradeIds: [referenceTrade.tradeId],
          ...(runDate ? { runDate } : {}),
        }),
      ).unwrap();

      toastMessage.success('Transaction amortization replaced successfully');

      return {
        updatedTradeIds: filteredTargetTradeIds,
      };
    } catch (error: any) {
      return thunkError(error, rejectWithValue, {
        prefix: 'Failed to replace transaction amortization in bulk',
      });
    }
  },
);
